import React from 'react';
import { Box, Breadcrumbs, Anchor } from '@mantine/core';
import { RevenueCatSubscriptionManager } from '../components/RevenueCatSubscriptionManager';
import { useRevenueCat } from '../contexts/RevenueCatContext';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';

/**
 * Page for managing subscription settings
 * Uses RevenueCat for subscription management
 */
export function SubscriptionPage() {
  const { isFree, isPremium, isTrialActive } = useRevenueCat();

  // User is "free" if not premium and not on trial
  const showUpgradeView = isFree && !isTrialActive;
  
  const breadcrumbItems = [
    { title: 'Home', href: '/' },
    { title: 'Subscription', href: '/settings/subscription' },
  ].map((item, index) => (
    <Anchor component={Link} to={item.href} key={index}>
      {item.title}
    </Anchor>
  ));

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Container with breadcrumbs */}
      <div className="container mx-auto px-4 pt-8 pb-16">
        <Box mb="lg">
          <Breadcrumbs>{breadcrumbItems}</Breadcrumbs>
        </Box>
        
        {/* Header section */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4 font-['Poppins']">
            {showUpgradeView ? (
              <>Upgrade to <span className="text-[#C2A4D6]">Voice Connect</span></>
            ) : (
              <>Your <span className="text-[#C2A4D6]">Subscription</span></>
            )}
          </h1>
          {showUpgradeView ? (
            <p className="text-lg text-gray-600 font-['Merriweather'] mb-4">
              Unlock voice conversations, mood tracking, and personalized insights
            </p>
          ) : (
            <p className="text-lg text-gray-600 font-['Merriweather'] mb-4">
              Manage your Voice Connect subscription
            </p>
          )}
        </div>

        {/* Subscription manager */}
        <div className="max-w-xl mx-auto mb-16">
          <RevenueCatSubscriptionManager />
        </div>
        
        {/* FAQ section with updated styling */}
        <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 font-['Poppins']">
            Subscription <span className="text-[#C2A4D6]">FAQ</span>
          </h2>

          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-3">What's included in Voice Connect?</h3>
            <div className="p-4 bg-[#C2A4D6]/10 rounded-lg">
              <ul className="space-y-2">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">AI voice conversations</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Unlimited text conversations</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Mood tracking and pattern analysis</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Personalized growth insights</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Extended conversation history</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Advanced emotional awareness tools</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Tailored wellness recommendations</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Is there a free trial?</h3>
              <p className="text-gray-600">
                Yes! New users get a 7-day free trial with full access to all Voice Connect features.
                No charge until your trial ends, and you can cancel anytime.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">How does billing work?</h3>
              <p className="text-gray-600">
                Choose between monthly ($19.99/month) or annual billing ($189.99/year - save 20%).
                Your subscription automatically renews at the end of each billing period.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Can I cancel my subscription?</h3>
              <p className="text-gray-600">
                Yes, you can cancel anytime. After cancellation, you'll keep access to all features
                until the end of your current billing period.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">What happens to my data if I cancel?</h3>
              <p className="text-gray-600">
                Your conversations and insights are saved. If you resubscribe later, you'll have
                access to your full history again.
              </p>
            </div>
          </div>
        </div>
        
        {/* Satisfaction guarantee */}
        <div className="mt-4 text-center max-w-3xl mx-auto">
          <div className="bg-white p-6 rounded-xl shadow-md">
            <h3 className="text-xl font-bold text-gray-800 mb-3">Our Satisfaction Guarantee</h3>
            <p className="text-gray-600">
              If you don't feel genuinely supported after your first 5 conversations, 
              we'll refund your subscription. No questions asked. Easy 2-click cancellation anytime.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SubscriptionPage;